import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  CR_META, CR_STEPS, CR_CLAUSES, CR_PLAYBOOK, CR_TASKS, CR_REPO, CR_PEOPLE, CR_THREAD_SEED,
  CR_DEPTS, CR_OPEN_ITEMS, CR_MEMO_POS, CrClause, CrMsg, Risk,
} from "./contractReviewData";

const C = {
  appBg: "#FAFAFB", white: "#FFFFFF",
  headerBorder: "#E4E6F3",
  searchBg: "#F7F6F9", searchBorder: "#D4D4D4", searchText: "#888888",
  assist1: "#6F63A5", assist2: "#41357B",
  bellDot: "#E0044E",
  avatarBg: "#3265AA",

  ink: "#0F1224",
  eyebrow: "#636B7A",
  ink2: "#1E1A3A",
  muted: "#6B668C",

  tabActive: "#110931", tabBorder: "#EBF0F5",
  grad1: "#ED338C", grad2: "#8026D9",
  cardBorder: "#EBF0F5",
  iconBox: "#F0EEFF", refresh: "#8026D9",
  healthyBg: "#EBFDFA", healthy: "#10B981",
  redlineBg: "#EEF2FF", redline: "#4F46E5",

  warn: "#F59E0B",
  selBorder: "#8026D9", selShadow: "rgba(128,38,217,0.08)",

  tableHeadBg: "#FAFAFB",
  statusActiveBg: "#EBFDFA", statusActive: "#10B981",
  statusExpiringBg: "#FFF1E6", statusExpiring: "#D97706",
  countBadgeBg: "#EEF2FF", countBadge: "#4F46E5",
  chipGreyBg: "#EBF0F5",
};

const TABS = ["Contract Review", "Playbook", "Tasks", "Repository"];

const BANNER = {
  title: "Icertis Contract Intelligence",
  sub: "Connected • 5 suppliers • 14 active contract records • last sync 07:12 today",
  healthy: "Connection healthy",
  redlines: "4 supplier redlines waiting",
  cta: "Continue to Dashboard",
};

const SUPPLIERS = [
  { name: "Precision Components Ltd", meta: "SUP-1042 • UK • Machined Components",
    detailMeta: "SUP-1042 • Machined Components • UK • annual spend £12.4m",
    risk: "Medium risk", riskKind: "med", contracts: "4 contracts - £12.4m", count: 4 },
  { name: "Nordwind Aerospace GmbH", meta: "SUP-2287 • Germany • Structures & Assemblies",
    detailMeta: "SUP-2287 • Structures & Assemblies • Germany • annual spend €21.8m",
    risk: "Low risk", riskKind: "low", contracts: "3 contracts - €21.8m", count: 3 },
  { name: "Kanto Precision KK", meta: "SUP-3310 • Japan • Fasteners",
    detailMeta: "SUP-3310 • Fasteners • Japan • annual spend $7.1m",
    risk: "Low risk", riskKind: "low", contracts: "2 contracts - $7.1m", count: 2 },
  { name: "Meridian Logistics Group", meta: "SUP-4471 • Netherlands • Freight & Logistics",
    detailMeta: "SUP-4471 • Freight & Logistics • Netherlands • annual spend €9.6m",
    risk: "High risk", riskKind: "high", contracts: "3 contracts - €9.6m", count: 3 },
];
const CONTRACTS = [
  { id: "CSA-2026-014", title: "Component Supply Agreement", warn: "Renewal - supplier markup awaiting review",
    type: "Supply", value: "£4.2m / 3 years", dates: "01 Sep 2023 - 31 Aug 2026",
    status: "Active", statusKind: "active", stage: "Counterparty • Supplier redline received • 1 redline", action: "primary", owner: "M. Okonjo — Procurement Executive" },
  { id: "NDA-2025-221", title: "Mutual Non-Disclosure Agreement",
    type: "NDA", value: "-", dates: "12 Feb 2025 - 11 Feb 2028",
    status: "Active", statusKind: "active", stage: "Own paper • Executed", action: "secondary", owner: "Legal — EMEA" },
  { id: "QAA-2024-087", title: "Quality Assurance Agreement",
    type: "Quality", value: "-", dates: "05 Jun 2024 - 04 Jun 2027",
    status: "Active", statusKind: "active", stage: "Own paper • Executed", action: "secondary", owner: "Quality — Aerostructures" },
  { id: "LTA-2023-455", title: "Long Term Pricing Agreement", warn: "Index clause due for refresh",
    type: "Pricing", value: "£1.8m / 2 years", dates: "01 Jan 2024 - 31 Dec 2026",
    status: "Expiring in 148 days", statusKind: "expiring", stage: "Own paper • Executed", action: "secondary", owner: "S. Rahman — Category Manager" },
];

const svg = (p: any) => ({ viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", ...p });
const Ic = {
  search: (p: any) => (<svg {...svg(p)}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  bell: (p: any) => (<svg {...svg(p)}><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M10 21a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>),
  sparkles: (p: any) => (<svg {...svg(p)}><path d="M12 3l1.8 4.9L18.7 9.7 13.8 11.5 12 16.4 10.2 11.5 5.3 9.7 10.2 7.9 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M19 14l.7 1.9 1.9.7-1.9.7L19 19.2 18.3 17.3 16.4 16.6l1.9-.7L19 14Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>),
  refresh: (p: any) => (<svg {...svg(p)}><path d="M20 11a8 8 0 0 0-14-4.5L4 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 4v4h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 13a8 8 0 0 0 14 4.5L20 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M20 20v-4h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  triangle: (p: any) => (<svg {...svg(p)}><path d="M12 4 2.5 20h19L12 4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M12 10v4M12 17v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  arrowRight: (p: any) => (<svg {...svg(p)}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  x: (p: any) => (<svg {...svg(p)}><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  arrowLeft: (p: any) => (<svg {...svg(p)}><path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  checkCircle: (p: any) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="m8 12 3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  clock: (p: any) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  circle: (p: any) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/></svg>),
  send: (p: any) => (<svg {...svg(p)}><path d="M3 11 21 3l-8 18-2-8-8-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>),
  download: (p: any) => (<svg {...svg(p)}><path d="M12 4v11M7 11l5 5 5-5M5 20h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  chevUp: (p: any) => (<svg {...svg(p)}><path d="m6 15 6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  chevDown: (p: any) => (<svg {...svg(p)}><path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  message: (p: any) => (<svg {...svg(p)}><path d="M4 5h16v11H9l-5 4V5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>),
  book: (p: any) => (<svg {...svg(p)}><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>),
  compare: (p: any) => (<svg {...svg(p)}><circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="2"/><circle cx="18" cy="18" r="2.5" stroke="currentColor" strokeWidth="2"/><path d="M6 8.5v4a4 4 0 0 0 4 4h5.5M13 6h3a2 2 0 0 1 2 2v7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  file: (p: any) => (<svg {...svg(p)}><path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M13 3v5h5M8 13h8M8 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
};

function PageHeader() {
  return ( 
    <>
        <nav className="breadcrumb" aria-label="Breadcrumb">
            <span>Contract</span>
        </nav>
        <div className="page-header">
            <h2 className="page-header__title">Contract Review</h2>
        </div>
    </>
  );
}

function Pill({ bg, color, children }: any) {
  return <span className="cr-pill" style={{ background: bg, color }}>{children}</span>;
}

function TabBar({ active, onChange }: any) {
  return (
    <div className="cr-tabbar" role="tablist" aria-label="Contract sections">
      {TABS.map((t) => {
        const on = active === t;
        return (
          <button key={t} role="tab" aria-selected={on} onClick={() => onChange(t)} className={on ? 'cr-tab cr-tab--active' : 'cr-tab'}>
            {t}
          </button>
        );
      })}
    </div>
  );
}

function IntegrationBanner({ onCta }: any) {
  return (
    <div className="cr-banner">
      <div className="cr-banner-left">
        <div className="cr-banner-icon"><Ic.refresh width={20} height={20} /></div>
        <div className="cr-banner-meta">
          <span className="cr-banner-title">{BANNER.title}</span>
          <span className="cr-banner-sub">{BANNER.sub}</span>
        </div>
      </div>
      <div className="cr-banner-right">
        <span className="cr-badge cr-badge--healthy">{BANNER.healthy}</span>
        <span className="cr-badge cr-badge--redline">{BANNER.redlines}</span>
        <button className="cr-cta" onClick={onCta}>{BANNER.cta}<Ic.arrowRight width={16} height={16} /></button>
      </div>
    </div>
  );
}

function SuppliersPanel({ selected, onSelect }: any) {
  return (
    <div className="cr-suppliers-panel">
      <div className="cr-suppliers-head">
        <span className="cr-suppliers-title">Suppliers</span>
        <span className="cr-suppliers-count">5</span>
      </div>
      <div className="cr-suppliers-list">
        {SUPPLIERS.map((s, i) => {
          const on = selected === i;
          return (
            <button key={i} className={on ? 'cr-supplier-card cr-supplier-card--selected' : 'cr-supplier-card'} onClick={() => onSelect(i)} aria-pressed={on}>
              <div className="cr-supplier-card-body">
                <div className="cr-supplier-name-wrap">
                  <span className="cr-supplier-name-text">{s.name}</span>
                  <span className="cr-supplier-meta-text">{s.meta}</span>
                </div>
                <div className="cr-supplier-badges">
                  <span className={"cr-risk-pill cr-risk-pill--" + s.riskKind}>{s.risk}</span>
                  <span className="cr-supplier-contracts">{s.contracts}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ContractsPanel({ supplier, isPrecision, onReview }: any) {
  return (
    <div className="cr-contracts-panel">
      <div className="cr-contracts-head">
        <div>
          <span className="cr-supplier-name">{supplier?.name}</span>
          <span className="cr-supplier-meta">{supplier?.detailMeta}</span>
        </div>
        <span className="cr-contract-count">{supplier?.count} active contracts in Icertis</span>
      </div>
      {isPrecision ? (
        <div className="cr-contracts-table-wrap">
          <div className="cr-contracts-table">
            <div className="cr-contracts-row cr-contracts-row--head">
              <div className="cr-col cr-col--id">CONTRACT ID</div>
              <div className="cr-col cr-col--title">TITLE</div>
              <div className="cr-col cr-col--type">TYPE</div>
              <div className="cr-col cr-col--value">VALUE</div>
              <div className="cr-col cr-col--status">STATUS</div>
              <div className="cr-col cr-col--stage">STAGE</div>
              <div className="cr-col cr-col--action">ACTION</div>
            </div>
            {CONTRACTS.map((c) => (
              <div key={c.id} className="cr-contracts-row">
                <div className="cr-col cr-col--id"><span className="cr-contract-id">{c.id}</span></div>
                <div className="cr-col cr-col--title">
                  <div className="cr-contract-title">{c.title}</div>
                  {c.warn && <div className="cr-contract-warn"><Ic.triangle width={12} height={12} />{c.warn}</div>}
                </div>
                <div className="cr-col cr-col--type">{c.type}</div>
                <div className="cr-col cr-col--value"><div className="cr-contract-value">{c.value}</div><div className="cr-contract-dates">{c.dates}</div></div>
                <div className="cr-col cr-col--status"><span className={c.statusKind === 'expiring' ? 'cr-status cr-status--expiring' : 'cr-status cr-status--active'}>{c.status}</span></div>
                <div className="cr-col cr-col--stage">{c.stage}</div>
                <div className="cr-col cr-col--action"><button className={c.action === 'primary' ? 'cr-btn cr-btn--primary' : 'cr-btn cr-btn--secondary'} onClick={() => onReview(c)}>Review with AI</button></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="cr-empty-contracts">
          <div className="cr-empty-icon"><Ic.file width={20} height={20} /></div>
          <span className="cr-empty-title">{supplier?.count} contract records for {supplier?.name}</span>
          <span className="cr-empty-sub">Select Precision Components Ltd to preview the Icertis contract set included in this view.</span>
        </div>
      )}
    </div>
  );
}

function Toast({ toast, onDismiss }: any) {
  if (!toast) return null;
  return (
    <div className="cr-toast" role="status" aria-live="polite">
      <div className="cr-toast-icon"><Ic.sparkles width={16} height={16} /></div>
      <div className="cr-toast-body">
        <span className="cr-toast-title">{toast.title}</span>
        {toast.sub && <span className="cr-toast-sub">{toast.sub}</span>}
      </div>
      <button onClick={onDismiss} aria-label="Dismiss" className="cr-toast-close"><Ic.x width={14} height={14} /></button>
    </div>
  );
}

/* ───────────────────────── Review workflow (ported from procureai-demo.html) ───────────────────────── */

interface ReviewMeta { id: string; title: string; counterparty: string; owner: string; value: string }
interface Instruction { ref: string; title: string; action: string; time: string }

const nowTime = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const RISK_KIND: Record<Risk, string> = { High: "red", Medium: "amber", Low: "green" };
const STATE_KIND: Record<string, string> = {
  Accepted: "green", Countered: "purple", Instructed: "purple", Escalated: "red", "In review": "amber", "Not reviewed": "amber",
};

function Tag({ kind, children }: { kind: string; children: React.ReactNode }) {
  return <span className={"cr-tag cr-tag--" + kind}>{children}</span>;
}
const riskTag = (r: Risk) => <Tag kind={RISK_KIND[r]}>{r}</Tag>;

function downloadFile(name: string, content: string, mime: string) {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function memoRow(c: CrClause): string[] {
  return CR_MEMO_POS[c.ref] ?? [
    c.risk === "Low" ? "Accepted" : c.risk === "Medium" ? "Partly accepted" : "Counter proposed",
    c.changed, c.rec, c.rationale,
    c.risk === "High" ? "Legal sign-off recorded" : c.risk === "Medium" ? "Finance approval required" : "None",
  ];
}

function StepCard({ icon, title, sub }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <div className="cr-step">
      <span className="cr-step-icon">{icon}</span>
      <div><div className="cr-step-title">{title}</div><div className="cr-step-sub">{sub}</div></div>
    </div>
  );
}

function AnalysingView({ meta, stepIdx, onBack }: { meta: ReviewMeta; stepIdx: number; onBack: () => void }) {
  return (
    <div className="cr-card cr-analysing">
      <div className="cr-analysing-head">
        <div>
          <span className="cr-eyebrow-sm">Contract intake</span>
          <div className="cr-analysing-title">{meta.title} <span className="cr-analysing-id">{meta.id}</span></div>
          <div className="cr-analysing-sub">{meta.counterparty} · {meta.owner} · {meta.value}</div>
        </div>
        <div className="cr-row-gap">
          <button className="cr-btn cr-btn--secondary cr-btn--sm" onClick={onBack}><Ic.arrowLeft width={12} height={12} /> Back to Icertis contracts</button>
          <Tag kind="green">Pulled from Icertis</Tag>
        </div>
      </div>
      <div className="cr-ai-panel">
        <div className="cr-ai-panel-head"><span className="cr-ai-badge"><Ic.sparkles width={11} height={11} /> AI</span><b>Analysing supplier changes against the org template and playbook</b></div>
        <div className="cr-thinking"><span className="cr-thinking-bar" /> Working…</div>
        <ul className="cr-analysing-steps">
          {CR_STEPS.slice(0, stepIdx + 1).map((s) => <li key={s}>{s}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Workspace({ meta, onBack, notify }: { meta: ReviewMeta; onBack: () => void; notify: (t: any) => void }) {
  const [selected, setSelected] = useState("11.2");
  const [filters, setFilters] = useState<Record<Risk, boolean>>({ High: true, Medium: true, Low: true });
  const [changesOnly, setChangesOnly] = useState(false);
  const [status, setStatus] = useState<Record<string, string>>({});
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [threads, setThreads] = useState<Record<string, CrMsg[]>>({});
  const [threadOpen, setThreadOpen] = useState(true);
  const [draft, setDraft] = useState("");
  const [signoff, setSignoff] = useState<Record<string, string>>({});
  const [signoffLog, setSignoffLog] = useState<{ dept: string; owner: string; status: string; time: string }[]>([]);
  const [instrOpen, setInstrOpen] = useState(false);
  const [instrText, setInstrText] = useState("");
  const [redlineGenerated, setRedlineGenerated] = useState(false);
  const [redlineTab, setRedlineTab] = useState<"preview" | "memo" | "open">("preview");

  const stateOf = (c: CrClause) => status[c.ref] || c.state;
  const threadOf = (ref: string) => threads[ref] ?? CR_THREAD_SEED[ref] ?? [];
  const sel = CR_CLAUSES.find((c) => c.ref === selected) || CR_CLAUSES[0];
  const visible = CR_CLAUSES.filter((c) => filters[c.risk] && (!changesOnly || c.edits > 0));
  const counts = { High: 0, Medium: 0, Low: 0 } as Record<Risk, number>;
  CR_CLAUSES.forEach((c) => counts[c.risk]++);
  const resolved = CR_CLAUSES.filter((c) => ["Accepted", "Countered", "Instructed", "Escalated"].includes(stateOf(c))).length;

  const logInstruction = (c: CrClause, action: string) =>
    setInstructions((p) => [...p.filter((i) => i.ref !== c.ref), { ref: c.ref, title: c.title, action, time: nowTime() }]);

  const act = (c: CrClause, kind: "accept" | "counter" | "escalate") => {
    const next = { accept: "Accepted", counter: "Countered", escalate: "Escalated" }[kind];
    const label = { accept: "Supplier wording accepted", counter: "Recommended counter applied", escalate: "Escalated to Legal" }[kind];
    setStatus((p) => ({ ...p, [c.ref]: next }));
    logInstruction(c, label);
    notify({ title: `Clause ${c.ref}`, sub: label });
  };
  const applyAll = () => {
    const st: Record<string, string> = {}; const list: Instruction[] = [];
    CR_CLAUSES.forEach((c) => {
      st[c.ref] = c.risk === "Low" ? "Accepted" : "Countered";
      list.push({ ref: c.ref, title: c.title, action: c.risk === "Low" ? "Supplier wording accepted" : "Recommended counter applied", time: nowTime() });
    });
    setStatus(st); setInstructions(list);
    notify({ title: "All recommended positions applied", sub: "12 clauses updated" });
  };
  const addInstruction = () => {
    const t = instrText.trim(); if (!t) return;
    setStatus((p) => ({ ...p, [sel.ref]: "Instructed" }));
    logInstruction(sel, t);
    setInstrText(""); setInstrOpen(false);
    notify({ title: `Clause ${sel.ref}`, sub: "Custom instruction captured for drafting" });
  };
  const post = () => {
    const t = draft.trim(); if (!t) return;
    setThreads((p) => ({ ...p, [sel.ref]: [...threadOf(sel.ref), { who: "M. Okonjo", time: "Today · " + nowTime(), text: t }] }));
    setDraft("");
  };
  const sign = (dept: string, owner: string, kind: "sign" | "changes") => {
    const s = kind === "sign" ? "Signed off" : "Changes requested";
    setSignoff((p) => ({ ...p, [dept]: s }));
    setSignoffLog((p) => [...p.filter((l) => l.dept !== dept), { dept, owner, status: s, time: nowTime() }]);
    notify({ title: dept, sub: s });
  };

  const downloadMemo = () => {
    const body = CR_CLAUSES.map((c) => {
      const m = memoRow(c);
      return `Clause ${c.ref} — ${c.title}\n  Position: ${m[0]}\n  Supplier position: ${m[1]}\n  AeroNova response: ${m[2]}\n  Rationale: ${m[3]}\n  Approval required: ${m[4]}\n`;
    }).join("\n");
    downloadFile(`${meta.id}_Change_Memorandum.txt`, `CHANGE MEMORANDUM — ${meta.title} (${meta.id})\nCounterparty: ${meta.counterparty}\n\n${body}`, "text/plain");
    notify({ title: "Change memorandum downloaded" });
  };
  const downloadRedline = () => {
    const html = `<html><head><meta charset="utf-8"><title>${meta.id} counter-redline v2</title></head><body style="font-family:Calibri,Arial,sans-serif">
<h2>${meta.title} — counter-redline v2</h2><p>AeroNova Industries plc and ${meta.counterparty}</p>
${CR_CLAUSES.map((c) => `<h4>Clause ${c.ref} · ${c.title}</h4><p>${c.body
  .replace(/<span class="cr-del">/g, '<span style="color:#c53030;text-decoration:line-through">')
  .replace(/<span class="cr-ins">/g, '<span style="color:#1d6fd0;text-decoration:underline">')}</p>`).join("")}</body></html>`;
    downloadFile(`AeroNova_${meta.id}_Counter_Redline_v2.doc`, html, "application/msword");
    notify({ title: "Counter-redline downloaded" });
  };

  const goTop = () => { window.scrollTo({ top: 0, behavior: "smooth" }); document.querySelector(".cr-root")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const metaLine = (
    <div className="cr-meta-line">
      <span>{meta.id} · {CR_META.version}</span><span>{meta.counterparty}</span><span>{meta.owner}</span><span>{CR_META.target}</span>
    </div>
  );

  /* ───────── sign-off table (shared by workspace + redline output) ───────── */
  const signoffDone = CR_DEPTS.filter((d) => signoff[d.d] === "Signed off").length;
  const SignoffCard = (
    <div className="cr-card cr-signoff">
      <div className="cr-signoff-head">
        <b>Department sign-off</b>
        <Tag kind={signoffDone === CR_DEPTS.length ? "green" : "amber"}>{signoffDone} of {CR_DEPTS.length} signed off</Tag>
        <span className="cr-muted-sm">All departments must sign off before redline v4 is issued to the counterparty.</span>
      </div>
      <div className="cr-tbl-wrap">
        <table className="cr-tbl">
          <thead><tr><th>Department</th><th>Approver</th><th>Review scope</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {CR_DEPTS.map((d) => {
              const st = signoff[d.d] || "Pending";
              return (
                <tr key={d.d}>
                  <td><b>{d.d}</b></td><td>{d.owner}</td><td>{d.scope}</td>
                  <td><Tag kind={st === "Signed off" ? "green" : st === "Changes requested" ? "red" : "amber"}>{st}</Tag></td>
                  <td className="cr-nowrap">
                    <button className="cr-mini cr-mini--ok" onClick={() => sign(d.d, d.owner, "sign")}>Sign off</button>
                    <button className="cr-mini" onClick={() => sign(d.d, d.owner, "changes")}>Request changes</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {signoffLog.length > 0 && (() => { const l = signoffLog[signoffLog.length - 1]; return <div className="cr-muted-sm cr-mt10">Latest: {l.dept} — {l.status} by {l.owner} at {l.time}</div>; })()}
    </div>
  );

  /* ───────── redline output ───────── */
  if (redlineGenerated) {
    const openCount = CR_OPEN_ITEMS.filter((o) => o.status === "Open").length;
    const returnBtn = (
      <button className="cr-btn cr-btn--secondary cr-nowrap" onClick={() => { setRedlineGenerated(false); goTop(); }}>
        <Ic.arrowLeft width={14} height={14} /> Return to review
      </button>
    );
    return (
      <>
        <div className="cr-card cr-mb14">
          <div className="cr-crumb">Contracts › Supply › {meta.id}</div>
          <div className="cr-ws-head">
            <div><div className="cr-ws-title">{meta.title}</div>{metaLine}</div>
            <div className="cr-row-gap">
              <Tag kind="purple">Counterparty paper</Tag><Tag kind="amber">In review</Tag>
              <button className="cr-btn cr-btn--secondary" onClick={downloadMemo}><Ic.file width={14} height={14} /> Export summary</button>
              {returnBtn}
            </div>
          </div>
          <div className="cr-steps-grid">
            <StepCard icon={<Ic.checkCircle width={16} height={16} />} title="Ingested" sub="42 pages, 118 clauses" />
            <StepCard icon={<Ic.checkCircle width={16} height={16} />} title="Analysed" sub="12 issues, 6 comments" />
            <StepCard icon={<Ic.checkCircle width={16} height={16} />} title="Instructions captured" sub={`${instructions.length} instructions logged`} />
            <StepCard icon={<Ic.checkCircle width={16} height={16} />} title="Redline generated" sub="v2 ready to send" />
          </div>
        </div>

        <div className="cr-card cr-mb14">
          <div className="cr-file-row">
            <span className="cr-file-icon"><Ic.file width={18} height={18} /></span>
            <div>
              <div className="cr-file-name">AeroNova_{meta.id}_Counter_Redline_v2.doc</div>
              <div className="cr-muted-sm">Based on {CR_META.redline} (supplier version 1) · Generated {nowTime()}</div>
            </div>
          </div>
          <div className="cr-row-gap cr-mt14">
            <button className="cr-btn cr-btn--primary" onClick={downloadRedline}><Ic.download width={14} height={14} /> Download redlined document</button>
            <button className="cr-btn cr-btn--secondary" onClick={downloadMemo}><Ic.file width={14} height={14} /> Download change memorandum</button>
            {returnBtn}
          </div>
          <div className="cr-outmeta">
            {[["Based on", "Supplier version 1", ""], ["Clauses updated", String(CR_CLAUSES.length), ""], ["Internal approvals", `${openCount} required`, openCount ? "amber" : ""], ["Residual risk", "Medium", "amber"], ["Created for", meta.owner.split(" — ")[0], ""]]
              .map((m) => <div key={m[0]}><div className="cr-k">{m[0]}</div><div className={"cr-v" + (m[2] ? " cr-v--" + m[2] : "")}>{m[1]}</div></div>)}
          </div>
          <div className="cr-ai-panel cr-mt14 cr-note">
            The redline shows the supplier's wording struck through and AeroNova's counter wording inserted against supplier version 1. Supplier comments are not carried across. Demo document — not for execution.
          </div>
        </div>

        <div className="cr-card">
          <div className="cr-subtabs">
            <button className={"cr-subtab" + (redlineTab === "preview" ? " cr-subtab--on" : "")} onClick={() => setRedlineTab("preview")}>Redline preview</button>
            <button className={"cr-subtab" + (redlineTab === "memo" ? " cr-subtab--on" : "")} onClick={() => setRedlineTab("memo")}>Change memorandum</button>
            <button className={"cr-subtab" + (redlineTab === "open" ? " cr-subtab--on" : "")} onClick={() => setRedlineTab("open")}>Open items <Tag kind="amber">{openCount}</Tag></button>
          </div>
          {redlineTab === "preview" && (
            <>
              <div className="cr-legend"><span><span className="cr-del">supplier wording</span> deleted</span><span><span className="cr-ins">AeroNova wording</span> inserted</span></div>
              <div className="cr-docpage">
                <div className="cr-eyebrow-sm">AeroNova_{meta.id}_Counter_Redline_v2.doc</div>
                <div className="cr-muted-sm cr-mb14">{meta.title} — counter-redline v2 · AeroNova Industries plc and {meta.counterparty}</div>
                {CR_CLAUSES.map((c) => (
                  <div key={c.ref} className="cr-docclause">
                    <div className="cr-eyebrow-sm">Clause {c.ref} · {c.title}</div>
                    <div className="cr-ctext" dangerouslySetInnerHTML={{ __html: c.body }} />
                  </div>
                ))}
              </div>
            </>
          )}
          {redlineTab === "memo" && (
            <div className="cr-tbl-wrap">
              <table className="cr-tbl">
                <thead><tr><th>Clause</th><th>Position</th><th>Supplier position</th><th>AeroNova response</th><th>Rationale</th><th>Approval required</th></tr></thead>
                <tbody>
                  {CR_CLAUSES.map((c) => {
                    const m = memoRow(c);
                    return (
                      <tr key={c.ref}>
                        <td><b>{c.ref}</b><div className="cr-muted-sm">{c.title}</div></td>
                        <td><Tag kind={m[0] === "Accepted" ? "green" : m[0] === "Partly accepted" ? "amber" : "purple"}>{m[0]}</Tag></td>
                        <td>{m[1]}</td><td>{m[2]}</td><td>{m[3]}</td>
                        <td>{m[4] === "None" ? <span className="cr-muted-sm">None</span> : <Tag kind={m[4].includes("required") ? "amber" : "green"}>{m[4]}</Tag>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {redlineTab === "open" && (
            <>
              {CR_OPEN_ITEMS.map((o) => (
                <div key={o.id} className="cr-openitem">
                  <div className="cr-row-gap"><b>{o.title}</b><Tag kind={o.cls}>{o.status}</Tag><span className="cr-muted-sm cr-ml-auto">{o.date}</span></div>
                  <div className="cr-muted-sm cr-mt4">{o.id} · Clause {o.clause} · Owner {o.owner}</div>
                  <div className="cr-openitem-text">{o.text}</div>
                </div>
              ))}
              <div className="cr-ai-panel cr-note">External release of the counter-redline is blocked until the Finance approval for Net 30 is cleared.</div>
            </>
          )}
        </div>
        <div className="cr-mt14">{SignoffCard}</div>
      </>
    );
  }

  /* ───────── review workspace ───────── */
  return (
    <>
      <div className="cr-card cr-mb14">
        <div className="cr-crumb">Contracts › Supply › {meta.id}</div>
        <div className="cr-ws-head">
          <div><div className="cr-ws-title">{meta.title}</div>{metaLine}</div>
          <div className="cr-row-gap">
            <button className="cr-btn cr-btn--secondary cr-btn--sm" onClick={onBack}><Ic.arrowLeft width={12} height={12} /> Back to Icertis contracts</button>
            <Tag kind="purple">Counterparty paper</Tag><Tag kind="amber">In review</Tag>
            <button className="cr-btn cr-btn--primary" onClick={() => { setRedlineGenerated(true); setRedlineTab("preview"); goTop(); notify({ title: "Counter-redline v2 generated", sub: `Attached to ${meta.id}` }); }}>
              <Ic.sparkles width={14} height={14} /> View Redline Details
            </button>
          </div>
        </div>
        <div className="cr-steps-grid">
          <StepCard icon={<Ic.checkCircle width={16} height={16} />} title="Ingested" sub="42 pages, 118 clauses" />
          <StepCard icon={<Ic.checkCircle width={16} height={16} />} title="Analysed" sub="12 issues, 6 comments" />
          <StepCard icon={<Ic.clock width={16} height={16} />} title="Instructions captured" sub={`${resolved} of 12 resolved`} />
          <StepCard icon={<Ic.circle width={16} height={16} />} title="Redline generated" sub="Not started" />
        </div>
      </div>

      <div className="cr-card cr-position">
        <span className="cr-eyebrow-sm">Overall position</span>
        <b className="cr-position-state">Material departures</b>
        <span className="cr-muted-sm">High risk <b>{counts.High}</b></span>
        <span className="cr-muted-sm">Medium risk <b>{counts.Medium}</b></span>
        <span className="cr-muted-sm">Low / acceptable <b>{counts.Low}</b></span>
        <span className="cr-muted-sm">Supplier comments <b>6</b></span>
        <div className="cr-position-right">
          <span className="cr-muted-sm">Filter</span>
          {(["High", "Medium", "Low"] as Risk[]).map((r) => (
            <button key={r} className={"cr-filter" + (filters[r] ? " cr-filter--on" : "")} onClick={() => setFilters((p) => ({ ...p, [r]: !p[r] }))}>{r}</button>
          ))}
          <button className={"cr-filter" + (changesOnly ? " cr-filter--on" : "")} onClick={() => setChangesOnly((v) => !v)}>Show changes only</button>
          <button className="cr-btn cr-btn--primary cr-btn--sm" onClick={() => setInstrOpen((v) => !v)}><Ic.sparkles width={13} height={13} /> Instruct AI</button>
        </div>
        {instrOpen && (
          <div className="cr-instr-bar">
            <input value={instrText} onChange={(e) => setInstrText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addInstruction()}
              placeholder={`Instruction for clause ${sel.ref} — e.g. "Hold 45 days, offer 2% rebate"`} />
            <button className="cr-btn cr-btn--primary cr-btn--sm" onClick={addInstruction}>Add</button>
          </div>
        )}
      </div>

      <div className="cr-ws-grid">
        <div className="cr-card cr-nav">
          <div className="cr-nav-head"><b>Clause navigator</b><span className="cr-muted-sm">{visible.length} clauses</span></div>
          {visible.map((c) => (
            <div key={c.ref} className={"cr-nav-item" + (c.ref === sel.ref ? " cr-nav-item--active" : "")} onClick={() => setSelected(c.ref)}>
              <div className="cr-nav-row"><span className="cr-nav-ref">{c.ref}</span>{riskTag(c.risk)}</div>
              <div className="cr-nav-title">{c.title}</div>
              <div className="cr-nav-sub"><span>✎ {c.edits}</span><span>💬 {c.comments}</span><span className="cr-ml-auto"><Tag kind={STATE_KIND[stateOf(c)] || "amber"}>{stateOf(c)}</Tag></span></div>
            </div>
          ))}
        </div>

        <div className="cr-card cr-doc">
          <div className="cr-doc-head">
            <span>{CR_META.redline}</span>
            <span className="cr-ml-auto cr-del-key">■ Deleted</span><span className="cr-ins-key">■ Supplier insertion</span>
          </div>
          {visible.map((c) => (
            <div key={c.ref} className={"cr-clause" + (c.ref === sel.ref ? " cr-clause--active" : "")} onClick={() => setSelected(c.ref)}>
              <div className="cr-clause-head"><span className="cr-eyebrow-sm">Clause {c.ref} · {c.title}</span>{riskTag(c.risk)}</div>
              <div className="cr-ctext" dangerouslySetInnerHTML={{ __html: c.body }} />
            </div>
          ))}
        </div>

        <div className="cr-card cr-ai-col">
          <div className="cr-ai-panel-head"><span className="cr-ai-badge cr-ai-badge--solid"><Ic.sparkles width={11} height={11} /> AI Analysis</span><span className="cr-muted-sm">Clause {sel.ref}</span></div>

          <div className="cr-sect"><Ic.compare width={12} height={12} /> What changed</div>
          <div className="cr-body-txt">{sel.changed}</div>

          <div className="cr-sect"><Ic.message width={12} height={12} /> {sel.comment ? `Supplier comment ${sel.comment.n}` : "Supplier comment"}</div>
          {sel.comment ? (
            <div className="cr-quote"><div className="cr-quote-who">{sel.comment.who}</div><div className="cr-muted-sm cr-mb6">{sel.comment.date}</div>{sel.comment.text}</div>
          ) : <div className="cr-muted-sm">No comment recorded against this clause.</div>}

          <div className="cr-sect cr-sect--click" onClick={() => setThreadOpen((v) => !v)}>
            <Ic.message width={12} height={12} /> Internal discussion <Tag kind="purple">{threadOf(sel.ref).length}</Tag>
            <span className="cr-ml-auto">{threadOpen ? <Ic.chevUp width={12} height={12} /> : <Ic.chevDown width={12} height={12} />}</span>
          </div>
          {threadOpen && (
            <div className="cr-thread">
              {threadOf(sel.ref).length ? threadOf(sel.ref).map((m, i) => {
                const p = CR_PEOPLE[m.who] || { dept: "Procurement", c: "#7c5cf0" };
                const ini = m.who.split(/[ .]+/).filter(Boolean).map((x) => x[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <div key={i} className="cr-msg">
                    <div className="cr-av" style={{ background: p.c }}>{ini}</div>
                    <div className="cr-msg-body">
                      <div className="cr-msg-head"><span className="cr-msg-who">{m.who}</span><span className="cr-msg-dept">{p.dept}</span><span className="cr-msg-time">{m.time}</span></div>
                      <div className="cr-msg-txt">{m.text}</div>
                    </div>
                  </div>
                );
              }) : <div className="cr-muted-sm cr-py10">No internal discussion on this clause yet.</div>}
              <div className="cr-compose">
                <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && post()} placeholder="Comment as M. Okonjo…" />
                <button onClick={post} aria-label="Post comment"><Ic.send width={14} height={14} /></button>
              </div>
            </div>
          )}

          <div className="cr-sect"><Ic.triangle width={12} height={12} /> Why it matters</div>
          <div className="cr-body-txt">{sel.why}</div>

          <div className="cr-sect"><Ic.book width={12} height={12} /> Playbook position</div>
          <div className="cr-quote"><div className="cr-quote-who">{sel.pb.ref}</div>{sel.pb.text}</div>

          <div className="cr-sect"><Ic.sparkles width={12} height={12} /> AI recommendation</div>
          <div className="cr-rec">
            {sel.rec}
            <div className="cr-rec-why"><b>Negotiation rationale.</b> {sel.rationale}</div>
          </div>
          <div className="cr-conf">
            <span className="cr-eyebrow-sm">Confidence</span>
            <div className="cr-conf-meter"><div className="cr-conf-fill" style={{ width: sel.conf + "%" }} /></div>
            <b>{sel.conf}%</b>
          </div>
          <button className="cr-act cr-act--primary" onClick={() => act(sel, "counter")}>Use recommended counter</button>
          <button className="cr-act" onClick={() => act(sel, "accept")}>Accept supplier wording</button>
          <button className="cr-act" onClick={() => setInstrOpen(true)}>Add instruction</button>
          <button className="cr-act" onClick={() => act(sel, "escalate")}>Escalate to Legal</button>
          <button className="cr-act" onClick={applyAll}>Apply all recommended positions</button>
        </div>
      </div>

      <div className="cr-mt14">{SignoffCard}</div>

      {instructions.length > 0 && (
        <div className="cr-card cr-mt14">
          <span className="cr-eyebrow-sm cr-mb10">Negotiation instruction log</span>
          <div className="cr-tbl-wrap">
            <table className="cr-tbl">
              <thead><tr><th>Clause</th><th>Title</th><th>Instruction</th><th>Time</th></tr></thead>
              <tbody>{instructions.map((i) => <tr key={i.ref}><td><b>{i.ref}</b></td><td>{i.title}</td><td>{i.action}</td><td>{i.time}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/* ───────── Playbook / Tasks / Repository tabs ───────── */
function PlaybookTab() {
  return (
    <div className="cr-card">
      <div className="cr-tab-title">Negotiation playbook</div>
      <div className="cr-muted-sm cr-mb14">AeroNova Industries — Procurement Legal EMEA · 6 active rules · last approved 12 Jun 2026</div>
      <div className="cr-tbl-wrap">
        <table className="cr-tbl">
          <thead><tr><th>Ref</th><th>Topic</th><th>Standard position</th><th>Approved fallback</th><th>Owner</th><th>Risk if breached</th></tr></thead>
          <tbody>{CR_PLAYBOOK.map((r) => <tr key={r[0]}><td><b>{r[0]}</b></td><td>{r[1]}</td><td>{r[2]}</td><td>{r[3]}</td><td>{r[4]}</td><td>{riskTag(r[5] as Risk)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
function TasksTab() {
  return (
    <div className="cr-card">
      <div className="cr-tab-title">Tasks</div>
      <div className="cr-muted-sm cr-mb14">5 open items assigned across review, approval and escalation</div>
      <div className="cr-tbl-wrap">
        <table className="cr-tbl">
          <thead><tr><th>ID</th><th>Task</th><th>Contract</th><th>Assignee</th><th>Due</th><th>Priority</th><th>State</th></tr></thead>
          <tbody>{CR_TASKS.map((t) => <tr key={t[0]}><td><b>{t[0]}</b></td><td>{t[1]}</td><td>{t[2]}</td><td>{t[3]}</td><td>{t[4]}</td><td>{riskTag(t[5] as Risk)}</td><td>{t[6]}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
function RepoTab() {
  return (
    <div className="cr-card">
      <div className="cr-tab-title">Repository</div>
      <div className="cr-muted-sm cr-mb14">6 agreements · AeroNova Industries EMEA portfolio</div>
      <div className="cr-tbl-wrap">
        <table className="cr-tbl">
          <thead><tr><th>ID</th><th>Agreement</th><th>Counterparty</th><th>Type</th><th>Value</th><th>Status</th><th>Risk</th><th>Renewal</th></tr></thead>
          <tbody>{CR_REPO.map((r) => <tr key={r[0]}><td><b>{r[0]}</b></td><td>{r[1]}</td><td>{r[2]}</td><td>{r[3]}</td><td>{r[4]}</td><td>{r[5]}</td><td>{riskTag(r[6] as Risk)}</td><td>{r[7]}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function ContractReview() {
  const [tab, setTab] = useState("Contract Review");
  const [selected, setSelected] = useState(0);
  const [toast, setToast] = useState<any>(null);
  const toastTimer = useRef<any>(null);
  const [contract, setContract] = useState<any>(null);
  const [running, setRunning] = useState(false);
  const [analysed, setAnalysed] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const fireToast = useCallback((t: any) => {
    setToast(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4200);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  // Simulated AI analysis: reveal each step, then open the workspace.
  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => {
      if (stepIdx + 1 >= CR_STEPS.length) { setRunning(false); setAnalysed(true); }
      else setStepIdx(stepIdx + 1);
    }, 650);
    return () => clearTimeout(t);
  }, [running, stepIdx]);

  const startReview = (c: any) => { setContract(c); setAnalysed(false); setStepIdx(0); setRunning(true); };
  const backToSource = () => { setContract(null); setAnalysed(false); setRunning(false); };

  const supplier = SUPPLIERS[selected];
  const isPrecision = selected === 0;
  const meta: ReviewMeta | null = contract && {
    id: contract.id, title: contract.title, counterparty: supplier.name, owner: contract.owner,
    value: contract.value === "-" ? "No committed value" : contract.value,
  };

  return (
    <div className="cr-root">
      <main className="cr-main">
        <div className="cr-container">
          <PageHeader />
          <TabBar active={tab} onChange={setTab} />

          {tab === "Playbook" ? <PlaybookTab />
            : tab === "Tasks" ? <TasksTab />
            : tab === "Repository" ? <RepoTab />
            : !meta ? (
              <>
                <IntegrationBanner onCta={() => fireToast({ title: "Continue to Dashboard", sub: "Opening the Icertis contract dashboard…" })} />
                <div className="cr-split">
                  <div className="cr-suppliers"><SuppliersPanel selected={selected} onSelect={setSelected} /></div>
                  <ContractsPanel supplier={supplier} isPrecision={isPrecision} onReview={startReview} />
                </div>
              </>
            ) : analysed ? (
              <Workspace key={meta.id} meta={meta} onBack={backToSource} notify={fireToast} />
            ) : (
              <AnalysingView meta={meta} stepIdx={stepIdx} onBack={backToSource} />
            )}
        </div>
      </main>
      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
